import { ISPGetItemContext } from '../interfaces/IGetServerData';
import { SimpleUri } from '@ms/odsp-utilities/lib/uri/SimpleUri';
import { getListApiUrl, getStaticApiUrl } from '../../utilities/list/ListApiUrlHelper';
import UriEncoding from '@ms/odsp-utilities/lib/encoding/UriEncoding';

const ENABLE_MODERN_LIST_SCHEMAS_PARAM = 'TryNewExperienceSingle';

export type IGetListDataAsStreamUrlParams = Pick<ISPGetItemContext,
  'webAbsoluteUrl' |
  'listFullUrl' |
  'rootFolder' |
  'pageToken' |
  'filterParams' |
  'sortField' |
  'sortDir' |
  'viewId' |
  'viewPath' |
  'groupString' |
  'searchTerm'> & {
  /**
   * Ensure that the 'modern' variant of the list schema is used for all calls.
   */
  enableModernListSchemas: boolean;
};

export function getListDataAsStreamUrl(context: IGetListDataAsStreamUrlParams): string {
  const {
    webAbsoluteUrl,
    listFullUrl,
    rootFolder,
    pageToken,
    filterParams,
    sortField,
    sortDir,
    viewId,
    viewPath,
    groupString,
    searchTerm,
    enableModernListSchemas
  } = context;

  const listUri = new SimpleUri(listFullUrl);

  const webUrl = webAbsoluteUrl || listUri.authority;

  if (!webUrl) {
    throw new Error('No web url available for GetList call');
  }

  let apiUrl = webAbsoluteUrl ?
    getListApiUrl({
      webAbsoluteUrl,
      listFullUrl: listFullUrl || `${new SimpleUri(webAbsoluteUrl).authority}${rootFolder}`
    })
      .segment('RenderListDataAsStream') :
    getStaticApiUrl({
      webAbsoluteUrl: webUrl
    })
      .segment('SP.List.GetListDataAsStream')
      .parameter('listFullUrl', listFullUrl);

  // if a request token is given, use it and return early
  if (pageToken) {
    apiUrl = apiUrl.rawParameter(pageToken.substring(1)); // strip off leading ?

    if (enableModernListSchemas) {
      apiUrl = apiUrl.parameter(ENABLE_MODERN_LIST_SCHEMAS_PARAM, { raw: 'TRUE' });
    }

    return apiUrl.toString();
  }

  // if no request token is given, build one
  if (rootFolder) {
    apiUrl = apiUrl.parameter('RootFolder', { raw: UriEncoding.encodeURIComponent(rootFolder) });
  }

  if (viewId) {
    apiUrl = apiUrl.parameter('View', { raw: viewId });
  }

  if (viewPath) {
    apiUrl = apiUrl.parameter('ViewPath', { raw: viewPath });
  }

  if (typeof groupString === 'string') {
    apiUrl = apiUrl
      .parameter('IsGroupRender', { raw: 'TRUE' })
      .parameter('DrillDown', 1)
      .parameter('GroupString', { raw: groupString });
  }

  if (typeof searchTerm !== 'undefined') {
    apiUrl = apiUrl
      .parameter('InplaceSearchQuery', searchTerm);
  }

  if (sortField) {
    apiUrl = apiUrl.parameter('SortField', { raw: sortField });
    if (sortDir) {
      apiUrl = apiUrl.parameter('SortDir', { raw: sortDir });
    }
  }

  if (filterParams) {
    apiUrl = apiUrl.rawParameter(filterParams);
  }

  if (enableModernListSchemas) {
    apiUrl = apiUrl.parameter(ENABLE_MODERN_LIST_SCHEMAS_PARAM, { raw: 'TRUE' });
  }

  return apiUrl.toString();
}
